import * as React from "react";
import { ConfigPaneKey } from "./config-categories";
import {
  IConfigSearchEntryDraft,
  IConfigSearchRegistry,
  nextConfigSearchId,
  NOOP_CONFIG_SEARCH_REGISTRY
} from "./config-search-index";

/**
 * How a setting gets into the search index, and how a search result gets back to it.
 *
 * Both directions run through context because the two ends are far apart and everything in
 * between belongs to somebody else: the containers own the form and the platform tab, the
 * five panes own their rail selection, and the ~140 leaf files in between are the ones
 * actually drawing the settings. Threading a prop through all of that would mean touching
 * every leaf twice — once to pass it down and again the next time a row moves.
 *
 * Registration is the same trick `config-change-tracking.tsx` already plays for the rail's
 * dots: a field is never asked what it is called or where it lives, it says so itself on
 * mount. Reveal is that read backwards — a target arrives from the search box, the pane
 * shell moves its rail to the right category, and the one part whose id matches scrolls
 * itself into view and marks itself.
 */

export const ConfigSearchIndexContext: React.Context<IConfigSearchRegistry> =
  React.createContext<IConfigSearchRegistry>(NOOP_CONFIG_SEARCH_REGISTRY);

/**
 * The search box, handed to the pane shell that should draw it above its rail.
 *
 * The box belongs at the top of the rail — it is the same directory the rail is, reached by
 * typing instead of by scanning — but the rail is drawn five times, once per platform pane,
 * and five boxes would be five queries and five sets of DOM ids. So the container puts the
 * node in this context for the ACTIVE pane only, and the other four shells see null.
 */
export const ConfigSearchSlotContext: React.Context<React.ReactNode> = React.createContext<React.ReactNode>(null);

/** The pane a subtree is being drawn inside. The containers set it in `renderPane`. */
export const ConfigPaneContext: React.Context<ConfigPaneKey | undefined> = React.createContext<
  ConfigPaneKey | undefined
>(undefined);

export interface IConfigRevealTarget {
  /** The entry id from the search result. Only the part carrying it reacts. */
  id: string;
  pane: ConfigPaneKey;
  categoryId?: string;
  /** The match was on a field name inside the row's dialog, so the dialog is the destination. */
  openDialog: boolean;
  /**
   * Bumped on every selection, so choosing the same result twice marks it twice. Without it
   * a second Enter on the row the reader is already looking at would do nothing visible.
   */
  token: number;
}

export const ConfigRevealContext: React.Context<IConfigRevealTarget | null> =
  React.createContext<IConfigRevealTarget | null>(null);

/** How long a revealed setting stays marked. Long enough to find, short enough to forget. */
const REVEAL_MS: number = 1800;

/** The attribute a reveal finds its element by. Spread onto the part's own root node. */
export interface IConfigSearchAnchorProps {
  "data-config-search-id": string;
}

export interface IConfigSearchTarget {
  /** Spread onto the element the reveal should scroll to. */
  anchorProps: IConfigSearchAnchorProps;
  /** True while this part is the one a search result just landed on. */
  revealed: boolean;
  /** True the moment a reveal for THIS part arrives, before the mark expires. */
  isRevealTarget: boolean;
  /**
   * A reveal for this part asked for its dialog: the match was on a field name only the
   * dialog holds, so landing on the row would stop one step short of the setting.
   */
  wantsDialog: boolean;
  /** The reveal's token, so a second request for the same row opens the dialog again. */
  revealToken: number;
}

export interface IConfigSearchTargetOptions {
  /**
   * Everything about the entry that can change, joined into one string. It is the effect's
   * real dependency: the draft is a fresh object on every render, and ~350 of these are
   * mounted inside a form that re-renders on every keystroke, so comparing the draft — by
   * identity or by serializing it — is work done three hundred times for nothing.
   */
  key: string;
  /**
   * False while the part is in a category the rail is not pointing at.
   *
   * A reveal into another category arrives BEFORE the shell has moved that rail, and an
   * element inside `display: none` cannot be scrolled to at all — the call is a no-op and
   * the mark lands on something the reader never sees. So the scroll waits for the section
   * to actually be on screen rather than for a frame to pass.
   */
  visible: boolean;
}

/** Register a section or a field with the search index, and answer when a result points here. */
export const useConfigSearchTarget = (
  draft: IConfigSearchEntryDraft | undefined,
  options: IConfigSearchTargetOptions
): IConfigSearchTarget => {
  const index: IConfigSearchRegistry = React.useContext(ConfigSearchIndexContext);
  const reveal: IConfigRevealTarget | null = React.useContext(ConfigRevealContext);

  const idRef: React.MutableRefObject<string | undefined> = React.useRef<string | undefined>(undefined);
  if (idRef.current === undefined) idRef.current = nextConfigSearchId();
  const id: string = idRef.current;

  const draftRef: React.MutableRefObject<IConfigSearchEntryDraft | undefined> = React.useRef<
    IConfigSearchEntryDraft | undefined
  >(draft);
  draftRef.current = draft;

  React.useEffect((): (() => void) | undefined => {
    const current: IConfigSearchEntryDraft | undefined = draftRef.current;
    if (!current) return undefined;
    index.register({ ...current, id });
    return (): void => index.unregister(id);
    // `options.key` stands in for the draft's contents, which the ref then supplies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, id, options.key]);

  const isRevealTarget: boolean = reveal !== null && reveal.id === id;
  const [revealed, setRevealed] = React.useState<boolean>(false);

  // Answered once per selection. A reveal is never cleared — the target stays in context for
  // the rest of the session — so without this, every later return to the category would
  // scroll to and flash a row nobody had just asked for.
  const handledToken: React.MutableRefObject<number> = React.useRef<number>(0);

  React.useEffect((): (() => void) | undefined => {
    if (!isRevealTarget || reveal === null || reveal.token === handledToken.current) return undefined;
    // A reveal into another category lands before the shell has moved that rail; this runs
    // again, with the same token, once the section it points at is the one being drawn — so
    // the token is only marked answered at the point the row can actually be shown.
    if (!options.visible) return undefined;
    handledToken.current = reveal.token;

    // One more frame, so the scroll reads a section the browser has actually laid out.
    const frame: number = window.requestAnimationFrame((): void => {
      const element: Element | null = document.querySelector(`[data-config-search-id="${id}"]`);
      // Always `nearest` on the inline axis: `scrollIntoView` walks every scrollable
      // ancestor, and centring horizontally shoves the whole editor sideways.
      if (element instanceof HTMLElement) element.scrollIntoView({ block: "center", inline: "nearest" });
      setRevealed(true);
    });
    const timer: ReturnType<typeof setTimeout> = setTimeout((): void => setRevealed(false), REVEAL_MS);

    return (): void => {
      window.cancelAnimationFrame(frame);
      clearTimeout(timer);
      setRevealed(false);
    };
    // The token is what makes choosing the same result twice mark it twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealTarget, reveal?.token, id, options.visible]);

  return {
    anchorProps: { "data-config-search-id": id },
    revealed,
    isRevealTarget,
    wantsDialog: isRevealTarget && reveal !== null && reveal.openDialog,
    revealToken: reveal?.token ?? 0
  };
};

/**
 * What a row's own children are told about a reveal that wants the dialog.
 *
 * `ConfigField` owns the entry id, so it is the only part that knows a target is addressed
 * here — but the dialog belongs to the leaf that drew the row, three components down. This
 * is how the one tells the other, without every row that has no dialog growing a prop for it.
 */
export interface IConfigFieldReveal {
  wantsDialog: boolean;
  token: number;
}

export const NO_FIELD_REVEAL: IConfigFieldReveal = { wantsDialog: false, token: 0 };

export const ConfigFieldRevealContext: React.Context<IConfigFieldReveal> =
  React.createContext<IConfigFieldReveal>(NO_FIELD_REVEAL);
